import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Collapse, Dialog, DialogActions,
  DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { requestAllocationExplanation, getAllocationExplanation } from '../services/api';
import { currency } from './AllocationMonitoring';

function Sources({ rows, title = 'View supporting entries' }) {
  const [open,setOpen]=useState(false);
  return <Box><Button onClick={()=>setOpen(!open)} aria-expanded={open}>{open?'Hide supporting entries':title}</Button>
    <Collapse in={open}><Stack spacing={1}>{rows.map(t=><Card variant="outlined" key={t.id}><CardContent>
      <Typography fontWeight="bold" sx={{overflowWrap:'anywhere'}}>{t.description || 'Transaction'}</Typography>
      <Typography>{t.date} · {currency(t.amount)}</Typography><Typography variant="caption">Entry #{t.id} · {t.role}</Typography>
    </CardContent></Card>)}</Stack></Collapse></Box>;
}

export default function AllocationExplanationDialog({ row, start, end, onClose }) {
  const [job,setJob]=useState(null),[error,setError]=useState(''),[attempt,setAttempt]=useState(0),[waiting,setWaiting]=useState(false);
  useEffect(()=>{
    let alive=true,timer;const began=Date.now();
    setJob(null);setError('');setWaiting(false);
    const update=value=>{
      if(!alive)return;
      setJob(value);
      if(['queued','processing'].includes(value.status)){
        if(Date.now()-began>120000){setWaiting(true);return;}
        timer=setTimeout(async()=>{
          try{update((await getAllocationExplanation(value.id)).data);}
          catch(e){if(alive)setError(e.response?.data?.msg||'Could not check this explanation. Open it again to check the saved request.');}
        },2500);
      }
    };
    requestAllocationExplanation(row.category_id,{start_date:start,end_date:end}).then(r=>update(r.data))
      .catch(e=>{if(alive)setError(e.response?.data?.msg||'Could not start the explanation. Your records are unchanged.');});
    return()=>{alive=false;clearTimeout(timer);};
  },[row.category_id,start,end,attempt]);
  const result=job?.result;
  return <Dialog open onClose={onClose} fullWidth maxWidth="sm" scroll="paper"><DialogTitle>Explain {row.category_name}</DialogTitle>
    <DialogContent dividers><Stack spacing={2}>
      <Typography color="text.secondary">{row.account_name} · {start} through {end}</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {!error && !result && (!job || ['queued','processing'].includes(job.status)) && <Alert severity="info" icon={waiting?false:<CircularProgress size={20}/>}>
        {waiting?'This is taking longer than usual. The request is saved; close this and open it again later.':'Preparing a read-only explanation. You can close this and continue using the app; open it again to see the result.'}
      </Alert>}
      {!error && job && ['failed','stale'].includes(job.status) && <Alert severity="warning">{job.error || 'The activity changed. Explain the updated records.'}</Alert>}
      {result && <>
        <Typography variant="h6">{result.headline}</Typography><Typography sx={{whiteSpace:'pre-line'}}>{result.summary}</Typography>
        <Card variant="outlined"><CardContent><Typography fontWeight="bold">Recorded activity</Typography>
          <Typography>Income contributions: {currency(result.actual.income)}</Typography><Typography>Total spending: {currency(result.actual.expenses)}</Typography>
          <Typography>Recorded balance change: {currency(result.actual.net)}</Typography><Typography>Balance at end date: {currency(result.actual.closing_balance)}</Typography>
        </CardContent></Card>
        {result.drivers.map((d,i)=><Card variant="outlined" key={i}><CardContent>
          <Typography fontWeight="bold">{i===0?'Main driver':'Also contributing'} · {currency(d.signed_amount)}</Typography>
          <Typography sx={{whiteSpace:'pre-line'}}>{d.explanation}</Typography><Sources rows={d.sources}/>
        </CardContent></Card>)}
        {result.counterfactual && <Alert severity="info"><Typography fontWeight="bold">Without the highlighted spending</Typography>
          <Typography>Spending would be {currency(result.counterfactual.spending_without)}; income less spending would be {currency(result.counterfactual.income_less_spending_without)}.</Typography>
          <Typography>Recorded balance change would be {currency(result.counterfactual.net_movement_without)} including transfers and other activity.</Typography>
          <Typography variant="body2">This comparison explains the effect. Every expense remains in your recorded totals.</Typography>
          <Sources rows={result.counterfactual.sources} title="View expenses in this comparison"/>
        </Alert>}
        <Typography fontWeight="bold">The longer pattern</Typography><Typography sx={{whiteSpace:'pre-line'}}>{result.trend}</Typography>
        {!!result.caveats.length && <Box><Typography fontWeight="bold">Worth checking</Typography><ul>{result.caveats.map((c,i)=><li key={i}><Typography>{c}</Typography></li>)}</ul></Box>}
        <Typography variant="caption" color="text.secondary">AI interpretation of recorded activity. Amount cards and source entries come from your ledger. Earlier months use monthly totals; detailed context covers {result.detail_scope.start} through {result.detail_scope.end}.</Typography>
      </>}
    </Stack></DialogContent><DialogActions>
      {(error || ['failed','stale'].includes(job?.status)) && <Button onClick={()=>setAttempt(a=>a+1)}>Try again</Button>}
      <Button onClick={onClose}>Close</Button>
    </DialogActions></Dialog>;
}
